import { useState } from "react";
import { Film, Trash2, Video } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

export const MAX_VIDEO = 100 * 1024 * 1024;
const MAX = MAX_VIDEO;

export const VideoInput = ({ testId, label, icon: Icon, capture, onFile, disabled }) => (
  <label data-testid={`${testId}-button`} className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#956842] cursor-pointer ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
    <Icon className="w-4 h-4" /> {label}
    <input data-testid={`${testId}-input`} type="file" accept="video/*" {...(capture ? { capture: "environment" } : {})} className="hidden"
      onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
  </label>
);

export const VideoManager = ({ product, onChange }) => {
  const [progress, setProgress] = useState(null);
  const videos = product.videos || [];

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX) { toast.error("Video troppo grande (max 100 MB): registra un video più breve"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      const { data } = await api.post(`/admin/products/${product.id}/videos`, fd, { onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)) });
      onChange(data);
      toast.success("Video caricato");
    } catch (e) { toast.error(e?.response?.status === 413 ? "Video troppo grande per il caricamento" : apiError(e)); } finally { setProgress(null); }
  };

  const remove = async (v) => {
    if (!window.confirm("Eliminare il video dalla scheda?")) return;
    try { onChange((await api.delete(`/admin/products/${product.id}/videos/${v.id}`)).data); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-6 pt-5 border-t border-[#D5B98C]/50" data-testid="video-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h4 className="text-xs uppercase tracking-widest text-[#39251D]">Video</h4>
        <div className="flex gap-4">
          <VideoInput testId="video-gallery" label="Scegli video" icon={Film} onFile={upload} disabled={progress !== null} />
          <VideoInput testId="video-camera" label="Registra" icon={Video} capture onFile={upload} disabled={progress !== null} />
        </div>
      </div>
      {progress !== null && (
        <div data-testid="video-upload-progress" className="mb-3">
          <div className="h-1.5 bg-[#E9DFCE]"><div className="h-full bg-[#956842] transition-[width]" style={{ width: `${progress}%` }} /></div>
          <p className="mt-1 text-[11px] text-[#242321]/60">Caricamento video… {progress}%</p>
        </div>
      )}
      {videos.length === 0 && progress === null && <p className="text-sm text-[#242321]/60" data-testid="video-empty">Nessun video. Facoltativo: un breve video mostra meglio il prodotto.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {videos.map((v, i) => (
          <div key={v.id} data-testid={`video-${i}`} className="relative border border-[#D5B98C]/60 bg-black">
            <video src={v.url} controls playsInline preload="metadata" className="w-full aspect-[4/5] object-contain" />
            <button data-testid={`video-delete-${i}`} onClick={() => remove(v)} className="absolute top-1.5 right-1.5 w-8 h-8 bg-[#F5F0E8]/90 flex items-center justify-center text-[#8a2f22]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-[#242321]/55">MP4, MOV o WEBM, max 100 MB. Il video appare nella galleria del prodotto dopo le foto; l'AI non lo elabora.</p>
    </div>
  );
};
